import * as ents from '@attain-apps/entities'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import { Cell } from './cell'
import type { CssConfigurable } from './css-configurable'

export type Column<
  TTypeParams extends
    ec.models.TypeParams.Constraint = ec.models.TypeParams.Constraint.Default,
> = Required<Column.Options<TTypeParams>>
export namespace Column {
  export interface Options<
    TTypeParams extends
      ec.models.TypeParams.Constraint = ec.models.TypeParams.Constraint,
  > extends CssConfigurable.Optionally {
    /**
     * TODO:
     *
     * @default false
     */
    isSortable?: boolean
    /**
     * TODO:
     *
     * @default 'left'
     */
    align?: Column.Align
    /**
     * @default flex: 1`
     */
    width?: ExactlyOneOf<{
      flex: number | string
      px: number | string
      /**
       * Ranges from `0 - 1`
       *
       * @example
       * ```txt
       * 0.5 => 50%
       * ```
       */
      percent: number
    }>
    /**
     * TODO:
     */
    cellConfig?: Cell.Factory<TTypeParams>
  }

  export const DEFAULT: Readonly<Column<any>> = {
    align: 'left',
    cellConfig: Cell.Factory.DEFAULT,
    css: {},
    isSortable: false,
    width: { flex: 1 },
  }

  export function from<TTypeParams extends ec.models.TypeParams.Constraint>(
    {
      align = DEFAULT.align,
      cellConfig = DEFAULT.cellConfig,
      css = DEFAULT.css,
      isSortable = DEFAULT.isSortable,
      width = DEFAULT.width,
    }: Column.Options<TTypeParams> = {},
    attribute?: ec.models.Attribute<TTypeParams>,
  ): Column<TTypeParams> {
    const defaultConfig: NoMissing<Column<TTypeParams>> = {
      align,
      cellConfig,
      css,
      isSortable,
      width,
    }

    if (!attribute) {
      return defaultConfig
    }

    if (
      ents.core.common.value.types.Intrinsic.ForNumber.isForNumber(
        attribute.valueInfo?.type,
      )
    ) {
      defaultConfig.align = 'right'
    } else if (
      ents.core.common.value.types.Intrinsic.isIntrinsic(
        attribute.valueInfo?.type,
      ) &&
      attribute.valueInfo.type ===
        ents.core.common.value.types.Intrinsic.Boolean
    ) {
      defaultConfig.align = 'center'
    }

    if (attribute.isSortable) {
      defaultConfig.isSortable = attribute.isSortable
    }

    return defaultConfig
  }

  export type Align = 'left' | 'center' | 'right'
}
